import { useEffect, useCallback } from 'react';
import { useInspectorNode } from '@/hooks/useInspectorNode';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { SliderField } from '@/components/inspector-fields/SliderField/SliderField';
import {
  ANIMATION_TYPES,
  ANIMATION_TYPE_LABELS,
  ANIMATION_EASINGS,
  ANIMATION_DEFAULTS,
  HOVER_EFFECT_TYPES,
  HOVER_EFFECT_LABELS,
  HOVER_DEFAULTS,
  HOVER_INTENSITY_CONFIG,
  HOVER_INTENSITY_DEFAULTS,
  IMAGE_CONTINUOUS_TYPES,
  IMAGE_CONTINUOUS_LABELS,
  CONTINUOUS_DEFAULTS,
  CONTINUOUS_SPEED_CONFIG,
  CONTINUOUS_INTENSITY_CONFIG,
  type AnimationType,
  type AnimationEasing,
  type HoverEffectType,
  type ImageContinuousType,
  type AnimationProps,
} from '@builder4/shared/types/animation';
import { ANIMATION_KEYFRAMES_CSS } from './keyframes';

interface NodeAnimationSettingsProps {
  showStagger?: boolean;
  showContinuousAnimation?: boolean;
}

const EASING_LABELS: Record<AnimationEasing, string> = {
  'ease-out': 'Ease Out',
  'ease-in': 'Ease In',
  'ease-in-out': 'Ease In Out',
  linear: 'Linear',
};

const KEYFRAME_MAP: Record<string, string> = {
  fadeIn: 'b4-fadeIn',
  slideUp: 'b4-slideUp',
  slideDown: 'b4-slideDown',
  slideLeft: 'b4-slideLeft',
  slideRight: 'b4-slideRight',
  zoomIn: 'b4-zoomIn',
  zoomOut: 'b4-zoomOut',
  flipUp: 'b4-flipUp',
  bounceIn: 'b4-bounceIn',
  blurIn: 'b4-blurIn',
  revealLeft: 'b4-revealLeft',
  revealRight: 'b4-revealRight',
  revealUp: 'b4-revealUp',
  revealDown: 'b4-revealDown',
};

const setContinuousVars = (
  el: HTMLElement,
  type: Exclude<ImageContinuousType, 'none'>,
  intensity: number,
) => {
  if (type === 'kenBurns') {
    const scale = 1 + intensity / 100;
    const translate = -(intensity / 10);
    el.style.setProperty('--b4-kb-scale', `${scale}`);
    el.style.setProperty('--b4-kb-tx', `${translate}%`);
    el.style.setProperty('--b4-kb-ty', `${translate}%`);
  } else if (type === 'slowZoom') {
    const scale = 1 + intensity / 100;
    el.style.setProperty('--b4-sz-scale', `${scale}`);
  } else if (type === 'pulse') {
    const opacity = 1 - intensity / 100;
    el.style.setProperty('--b4-pulse-opacity', `${opacity}`);
  }
};

const NodeAnimationSettings = ({
  showStagger = false,
  showContinuousAnimation = false,
}: NodeAnimationSettingsProps = {}) => {
  const { id, props, actions, node } = useInspectorNode<Partial<AnimationProps>>();
  const displayName = node?.data?.displayName ?? 'Node';
  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);

  const animationType = getBPProp(props, 'animationType') ?? ANIMATION_DEFAULTS.animationType;
  const animationDuration =
    getBPProp(props, 'animationDuration') ?? ANIMATION_DEFAULTS.animationDuration;
  const animationDelay = getBPProp(props, 'animationDelay') ?? ANIMATION_DEFAULTS.animationDelay;
  const animationEasing = getBPProp(props, 'animationEasing') ?? ANIMATION_DEFAULTS.animationEasing;
  const hoverEffect = getBPProp(props, 'hoverEffect') ?? HOVER_DEFAULTS.hoverEffect;
  const hoverTransitionDuration =
    getBPProp(props, 'hoverTransitionDuration') ?? HOVER_DEFAULTS.hoverTransitionDuration;
  const hoverIntensity = getBPProp(props, 'hoverIntensity');
  const staggerDelay = getBPProp(props, 'staggerDelay') ?? 0;
  const continuousAnimation = getBPProp(props, 'continuousAnimation') ?? 'none';
  const continuousSpeed = getBPProp(props, 'continuousSpeed');
  const continuousIntensity = getBPProp(props, 'continuousIntensity');

  useEffect(() => {
    const styleId = 'b4-animation-keyframes-builder';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = ANIMATION_KEYFRAMES_CSS;
    document.head.appendChild(style);
  }, []);

  const handlePreview = useCallback(() => {
    const el = document.querySelector(`[data-animation-preview="${id}"]`) as HTMLElement | null;
    if (!el) return;

    const hasEntrance = animationType !== 'none';
    const hasContinuous = continuousAnimation !== 'none';
    const hasStagger = staggerDelay > 0;

    if (!hasEntrance && !hasContinuous && !hasStagger) return;

    // Preview entrance animation on the element itself
    if (hasEntrance) {
      const keyframe = KEYFRAME_MAP[animationType];
      if (keyframe) {
        el.style.animation = 'none';
        void el.offsetHeight;
        el.style.animation = `${keyframe} ${animationDuration}ms ${animationEasing} ${animationDelay}ms both`;
        el.addEventListener(
          'animationend',
          () => {
            el.style.animation = '';
          },
          { once: true },
        );
      }
    }

    // Preview stagger on children
    if (hasStagger) {
      const children = el.querySelectorAll('[data-animation-preview]') as NodeListOf<HTMLElement>;
      children.forEach((child, index) => {
        const childAnimType = child.getAttribute('data-animation') || child.dataset.animation;
        const childKeyframe = childAnimType ? KEYFRAME_MAP[childAnimType] : undefined;
        if (!childKeyframe) return;

        const childDuration = parseInt(child.getAttribute('data-animation-duration') || '600', 10);
        const childEasing = child.getAttribute('data-animation-easing') || 'ease-out';
        const childBaseDelay = parseInt(child.getAttribute('data-animation-delay') || '0', 10);
        const totalDelay = childBaseDelay + index * staggerDelay;

        child.style.animation = 'none';
        void child.offsetHeight;
        child.style.animation = `${childKeyframe} ${childDuration}ms ${childEasing} ${totalDelay}ms both`;
        child.addEventListener(
          'animationend',
          () => {
            child.style.animation = '';
          },
          { once: true },
        );
      });
    }

    // Preview continuous animation
    if (hasContinuous) {
      const contType = continuousAnimation as Exclude<ImageContinuousType, 'none'>;
      const defaults = CONTINUOUS_DEFAULTS[contType];
      const speed = continuousSpeed ?? defaults.speed;
      const intensity = continuousIntensity ?? defaults.intensity;
      const animCss = `b4-${continuousAnimation} ${speed}s ease-in-out infinite`;

      const prevAnimation = el.style.animation;
      const startDelay = hasEntrance ? animationDuration + animationDelay + 100 : 0;
      setTimeout(() => {
        setContinuousVars(el, contType, intensity);
        el.style.animation = animCss;
        const cycleDuration = speed * 1000;
        setTimeout(() => {
          el.style.animation = prevAnimation || '';
        }, cycleDuration);
      }, startDelay);
    }
  }, [
    id,
    animationType,
    animationDuration,
    animationDelay,
    animationEasing,
    staggerDelay,
    continuousAnimation,
    continuousSpeed,
    continuousIntensity,
  ]);

  const hasAnimation = animationType !== 'none';
  const hasHoverEffect = hoverEffect !== 'none';
  const hasContinuousAnim = continuousAnimation !== 'none';
  const hasStaggerValue = staggerDelay > 0;
  const hasAnyPreviewable = hasAnimation || hasContinuousAnim || hasStaggerValue;

  return (
    <div className="flex flex-col gap-3">
      <p className="pb-1 text-xs font-semibold text-neutral-900">Animation</p>

      {/* Entrance Animation */}
      <SelectField
        text="Type"
        defaultValue={animationType}
        onValueChange={(value) => {
          actions.setProp(id, (p: Partial<AnimationProps>) => {
            setBPProp(p, 'animationType', value as AnimationType);
          });
        }}
        size="small"
      >
        {ANIMATION_TYPES.map((type) => (
          <SelectItem key={type} value={type}>
            {ANIMATION_TYPE_LABELS[type]}
          </SelectItem>
        ))}
      </SelectField>

      {hasAnimation && (
        <>
          <SliderField
            label="Duration"
            value={animationDuration}
            onChange={() => {}}
            onChangeComplete={(value) => {
              actions.setProp(id, (p: Partial<AnimationProps>) => {
                setBPProp(p, 'animationDuration', value);
              });
            }}
            min={100}
            max={2000}
            step={50}
            suffix="ms"
          />
          <SliderField
            label="Delay"
            value={animationDelay}
            onChange={() => {}}
            onChangeComplete={(value) => {
              actions.setProp(id, (p: Partial<AnimationProps>) => {
                setBPProp(p, 'animationDelay', value);
              });
            }}
            min={0}
            max={2000}
            step={50}
            suffix="ms"
          />
          <SelectField
            text="Easing"
            defaultValue={animationEasing}
            onValueChange={(value) => {
              actions.setProp(id, (p: Partial<AnimationProps>) => {
                setBPProp(p, 'animationEasing', value as AnimationEasing);
              });
            }}
            size="small"
          >
            {ANIMATION_EASINGS.map((easing) => (
              <SelectItem key={easing} value={easing}>
                {EASING_LABELS[easing]}
              </SelectItem>
            ))}
          </SelectField>
        </>
      )}

      {/* Hover Effect */}
      <SelectField
        text="Hover Effect"
        defaultValue={hoverEffect}
        onValueChange={(value) => {
          const newEffect = value as HoverEffectType;
          actions.setProp(id, (p: Partial<AnimationProps>) => {
            setBPProp(p, 'hoverEffect', newEffect);
            setBPProp(
              p,
              'hoverIntensity',
              newEffect !== 'none' ? HOVER_INTENSITY_DEFAULTS[newEffect] : undefined,
            );
          });
        }}
        size="small"
      >
        {HOVER_EFFECT_TYPES.map((type) => (
          <SelectItem key={type} value={type}>
            {HOVER_EFFECT_LABELS[type]}
          </SelectItem>
        ))}
      </SelectField>

      {hasHoverEffect && (
        <>
          <SliderField
            label="Duration"
            value={hoverTransitionDuration}
            onChange={() => {}}
            onChangeComplete={(value) => {
              actions.setProp(id, (p: Partial<AnimationProps>) => {
                setBPProp(p, 'hoverTransitionDuration', value);
              });
            }}
            min={50}
            max={1000}
            step={50}
            suffix="ms"
          />
          {(() => {
            const config = HOVER_INTENSITY_CONFIG[hoverEffect as Exclude<HoverEffectType, 'none'>];
            return (
              <SliderField
                label={config.label}
                value={hoverIntensity ?? config.default}
                onChange={() => {}}
                onChangeComplete={(value) => {
                  actions.setProp(id, (p: Partial<AnimationProps>) => {
                    setBPProp(p, 'hoverIntensity', value);
                  });
                }}
                min={config.min}
                max={config.max}
                step={config.step}
                suffix={config.suffix}
              />
            );
          })()}
        </>
      )}

      {/* Stagger Children (Container only) */}
      {showStagger && (
        <SliderField
          label="Stagger Delay"
          value={staggerDelay}
          onChange={() => {}}
          onChangeComplete={(value) => {
            actions.setProp(id, (p: Partial<AnimationProps>) => {
              setBPProp(p, 'staggerDelay', value);
            });
          }}
          min={0}
          max={500}
          step={25}
          suffix="ms"
        />
      )}

      {/* Continuous Animation (Image only) */}
      {showContinuousAnimation && (
        <>
          <SelectField
            text="Continuous"
            defaultValue={continuousAnimation}
            onValueChange={(value) => {
              const newType = value as ImageContinuousType;
              actions.setProp(id, (p: Partial<AnimationProps>) => {
                setBPProp(p, 'continuousAnimation', newType);
                if (newType !== 'none') {
                  const defaults = CONTINUOUS_DEFAULTS[newType];
                  setBPProp(p, 'continuousSpeed', defaults.speed);
                  setBPProp(p, 'continuousIntensity', defaults.intensity);
                } else {
                  setBPProp(p, 'continuousSpeed', undefined);
                  setBPProp(p, 'continuousIntensity', undefined);
                }
              });
            }}
            size="small"
          >
            {IMAGE_CONTINUOUS_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {IMAGE_CONTINUOUS_LABELS[type]}
              </SelectItem>
            ))}
          </SelectField>
          {hasContinuousAnim &&
            (() => {
              const contType = continuousAnimation as Exclude<ImageContinuousType, 'none'>;
              const speedConfig = CONTINUOUS_SPEED_CONFIG[contType];
              const intensityConfig = CONTINUOUS_INTENSITY_CONFIG[contType];
              const defaults = CONTINUOUS_DEFAULTS[contType];
              return (
                <>
                  <SliderField
                    label="Speed"
                    value={continuousSpeed ?? defaults.speed}
                    onChange={() => {}}
                    onChangeComplete={(value) => {
                      actions.setProp(id, (p: Partial<AnimationProps>) => {
                        setBPProp(p, 'continuousSpeed', value);
                      });
                    }}
                    min={speedConfig.min}
                    max={speedConfig.max}
                    step={speedConfig.step}
                    suffix="s"
                  />
                  <SliderField
                    label={intensityConfig.label}
                    value={continuousIntensity ?? defaults.intensity}
                    onChange={() => {}}
                    onChangeComplete={(value) => {
                      actions.setProp(id, (p: Partial<AnimationProps>) => {
                        setBPProp(p, 'continuousIntensity', value);
                      });
                    }}
                    min={intensityConfig.min}
                    max={intensityConfig.max}
                    step={intensityConfig.step}
                    suffix={intensityConfig.suffix}
                  />
                </>
              );
            })()}
        </>
      )}

      {/* Preview button — previews all except hover */}
      {hasAnyPreviewable && (
        <button
          type="button"
          onClick={handlePreview}
          className="h-8 w-full cursor-pointer rounded-md border border-neutral-200 bg-white text-xs font-medium text-neutral-700 hover:bg-neutral-50"
        >
          Preview Animation
        </button>
      )}
    </div>
  );
};

export default NodeAnimationSettings;
